import { Call, Device } from '@twilio/voice-sdk';
import axios from 'axios';
import { Hash, Mic, MicOff, MoreVertical, PhoneForwarded, PhoneOff, ShieldOff } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { setActiveConversation } from '@/actions/webchat';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useActiveSpeakerDetection } from '@/hooks/useActiveSpeakerDetection';
import { useDraggable } from '@/hooks/useDraggable';
import { useIncomingCallAlerts } from '@/hooks/useIncomingCallAlerts';
import { cn } from '@/lib/utils';
import type { RootState } from '@/reducers';
import {
  setCallInfo,
  setCustomer,
  setForwardingStatus,
  setForwardingTargetUser,
  setStatus,
  setVisible,
} from '@/reducers/voiceReducer';
import type { TwilioCallWithParams } from '@/types';
import type { Call as ServerCall } from '@/types/validationSchemas/CallSchema';
import { isCustomerOnActiveCall } from '@/utils/activeCustomerCall';
import { buildCanonicalCustomerConversationPath } from '@/utils/customerConversationRoutes';
import formatPhone from '@/utils/formatPhoneForUser';
import {
  classifyMicrophoneError,
  type MicrophoneAccessResult,
  type MicrophoneIssue,
  probeMicrophoneAccess,
} from '@/utils/microphoneAccess';
import ForwardCallPanel from './ForwardCallPanel';
import IncomingCallDialog from './IncomingCallDialog';
import MicrophonePermissionDialog from './MicrophonePermissionDialog';
import { type CallTone, PhoneCallScreen } from './PhoneCallScreen';
import { useCompactCallUi } from './useCompactCallUi';

const TOKEN_REFRESH_LEAD_MS = 60_000;
const TOKEN_REFRESH_RETRY_MS = 15_000;
const TOKEN_RECOVERY_THROTTLE_MS = 60_000;
// AccessTokenInvalid, AccessTokenExpired, AuthenticationFailed.
const DEVICE_TOKEN_ERROR_CODES = new Set([20101, 20104, 20151]);
// Waits between attempts to get a registered device; the last one repeats.
const DEVICE_RETRY_DELAYS_MS = [5_000, 15_000, 60_000, 120_000];
// What the SDK raises when its signalling websocket drops or cannot send:
// 31005 (socket closed abnormally), 31009 (no transport to send on) and 53001
// (signalling disconnected). A bare socket error arrives as a generic 31000
// that wraps a 53001, hence the look at the wrapped error too.
const TRANSIENT_SIGNALING_ERROR_CODES = new Set([31005, 31009, 53001]);
const SIGNALING_DOWN_WARNING_MS = 60_000;

type DeviceError = Error & {
  code?: number;
  originalError?: { twilioError?: { code?: number } };
};

const isTransientSignalingError = (err: DeviceError | null | undefined): boolean =>
  TRANSIENT_SIGNALING_ERROR_CODES.has(err?.code ?? 0) ||
  TRANSIENT_SIGNALING_ERROR_CODES.has(err?.originalError?.twilioError?.code ?? 0);

// A refused token (signed out, no access to the business, voice not enabled)
// does not fix itself by waiting, unlike a network failure or a server error.
const isTokenRequestRefused = (err: unknown): boolean =>
  axios.isAxiosError(err) && [401, 403, 404].includes(err.response?.status ?? 0);

// An incoming call can only be answered while it is 'pending'. It leaves that
// state when the caller hangs up or someone else answers ('cancel'), when it
// is declined here ('reject'), when the signalling socket drops under it, or
// silently when the device ignores it to place an outbound call.
const isCallPending = (call: Call): boolean => call.status() === Call.State.Pending;

const findPendingCall = (device: Device | null, except?: Call): Call | null =>
  device?.calls.find((call) => call !== except && isCallPending(call)) ?? null;

// Which pending call the microphone recovery dialog should resume on retry.
type MicGate = {
  issue: MicrophoneIssue;
  context: 'outbound' | 'incoming' | null;
};

const FloatingStatusBar = () => {
  const log = useCallback((...args: unknown[]): void => console.log('[Voice]', ...args), []);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    visible,
    customer,
    status,
    ongoingCalls,
    forwardingStatus,
    forwardingTargetUser,
    callInfo,
  } = useSelector((state: RootState) => state.voice);
  const customerConversations = useSelector(
    (state: RootState) => state.webchat.customerConversations,
  );
  const auth = useSelector((state: RootState) => state.auth);

  const hasVoiceProduct = !!auth?.products?.includes('voice');
  const [showBlockDialog, setShowBlockDialog] = useState<boolean>(false);
  const deviceRef = useRef<Device | null>(null);
  const callRef = useRef<Call | null>(null);
  const initiatedRef = useRef<boolean>(false);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [callDuration, setCallDuration] = useState<number>(0);
  const callTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [incomingCall, setIncomingCall] = useState<Call | null>(null);
  // The call whose Accept is waiting on the microphone check. Guards against a
  // second click, and lets Decline withdraw the accept while it waits.
  const acceptingCallRef = useRef<Call | null>(null);
  const [isAcceptingIncoming, setIsAcceptingIncoming] = useState<boolean>(false);
  const deviceInitializedRef = useRef<boolean>(false);
  const [showDismissCountdown, setShowDismissCountdown] = useState(true);
  const [dismissProgress, setDismissProgress] = useState(100);
  const dismissTimerRef = useRef<NodeJS.Timeout | null>(null);
  const handledForwardingTerminalRef = useRef<string | null>(null);
  const forwardingStatusRef = useRef(forwardingStatus);
  const [isKeypadOpen, setIsKeypadOpen] = useState(false);
  const [keypadDigits, setKeypadDigits] = useState('');
  const [isForwardPanelOpen, setIsForwardPanelOpen] = useState(false);
  // Microphone gate: calls are not reserved/accepted until a getUserMedia
  // probe succeeds. micGate drives the recovery dialog when access is blocked;
  // isMicProbePending is true while the probe (and possibly the browser's
  // native permission prompt) is unresolved.
  const [micGate, setMicGate] = useState<MicGate | null>(null);
  const [isMicRetrying, setIsMicRetrying] = useState<boolean>(false);
  const [isMicProbePending, setIsMicProbePending] = useState<boolean>(false);

  const isForwardingInProgress =
    forwardingStatus === 'initiated' ||
    forwardingStatus === 'ringing';
  const isForwardingTerminal =
    forwardingStatus === 'answered' ||
    forwardingStatus === 'failed' ||
    forwardingStatus === 'canceled';
  const isForwardingActive = isForwardingInProgress || isForwardingTerminal;

  // On a recorded outbound call the customer's leg is answered a few seconds
  // before ours is bridged: they hear the recording notice first while this
  // side still hears ringing. The server's Call record flips to in-progress
  // at their pickup, so surface that instead of leaving the agent guessing
  // (or hanging up on a customer who is already on the line).
  const serverCall = callInfo as { callStatus?: string; direction?: string } | null;
  const hasCustomerAnsweredBeforeBridge =
    status === 'ringing' &&
    serverCall?.direction === 'outbound' &&
    (serverCall.callStatus === 'in-progress' || serverCall.callStatus === 'answered') &&
    isCustomerOnActiveCall([callInfo as ServerCall], customer);

  useEffect(() => {
    forwardingStatusRef.current = forwardingStatus;
  }, [forwardingStatus]);

  // Use draggable hook for position management
  const { position, startDrag } = useDraggable({
    defaultPosition: { rightPercent: 1.5, topPercent: 1.5 },
  });

  // Phones (and phones in landscape) get a full-screen call screen that can be
  // minimised to a bar instead of the draggable card. Whether it is expanded
  // lives here so rotating to a layout with the card and back keeps it.
  const isCompactCallUi = useCompactCallUi();
  const [isCallScreenExpanded, setIsCallScreenExpanded] = useState<boolean>(true);

  // Every new call (placed or accepted) opens the full call screen.
  useEffect(() => {
    if (visible && status === 'connecting') setIsCallScreenExpanded(true);
  }, [visible, status]);

  // Active speaker detection
  const isCustomerSpeaking = useActiveSpeakerDetection(
    hasVoiceProduct && status === 'answered' ? callRef.current : null,
  );

  const callSetupTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastTokenRecoveryRef = useRef<number>(0);
  const signalingDownSinceRef = useRef<number | null>(null);
  const signalingDownWarnedRef = useRef<boolean>(false);
  const deviceRebuildPendingRef = useRef<boolean>(false);
  const rebuildDeviceRef = useRef<(() => void) | null>(null);

  const getAccessToken = useCallback(async (): Promise<string> => {
    log('requesting access token');
    try {
      const res = await axios.get('/api/voice/token', {
        params: {
          platform: 'web',
        },
      });
      const token = res.data?.token;
      log('received access token');
      return token;
    } catch (err) {
      log('Failed to get access token', err);
      // Check if it's a configuration error
      if (axios.isAxiosError(err) && err.response?.data?.details) {
        console.error('Voice configuration error:', err.response.data.details);
        toast.error(
          `Voice configuration error: ${err.response.data.details}. Please contact your administrator.`,
        );
      }
      throw err; // Re-throw to maintain existing error handling
    }
  }, [log]);

  const clearDismissTimer = useCallback((): void => {
    if (dismissTimerRef.current) {
      clearInterval(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
  }, []);

  const pendingReservationRef = useRef<{ phone: string; reservationToken: string } | null>(null);
  // Bumped whenever the user tears an attempt down so an in-flight placeCall
  // can tell its attempt was canceled and must not go on to dial.
  const callAttemptRef = useRef(0);

  // Server-side release only deletes the lock while it is still an unconsumed
  // reservation (no callSid), so firing this after the call connected is a
  // harmless no-op. Every path that abandons a call attempt must call this,
  // otherwise the leaked reservation blocks redials for up to a minute.
  const releasePendingReservation = useCallback((): void => {
    const pending = pendingReservationRef.current;
    if (!pending) return;
    pendingReservationRef.current = null;
    axios
      .post('/api/voice/outbound-reservation/release', pending)
      .catch((releaseError) => log('failed to release call reservation', releaseError));
  }, [log]);

  const resetCallState = useCallback((): void => {
    try {
      callRef.current?.disconnect();
    } catch {
      // call may already be disconnected
    }
    callRef.current = null;
    setIsMuted(false);
    setCallDuration(0);
    setIsKeypadOpen(false);
    setKeypadDigits('');
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }
  }, []);

  // Several callers can ring at once but only one is shown. The shown call
  // keeps its place while it is still ringing; once it has gone (pass it as
  // `ended` when its status has not caught up yet) the next caller still
  // ringing takes over, or the dialog closes.
  const refreshIncomingCall = useCallback((ended?: Call): void => {
    setIncomingCall((shown) => {
      if (shown && shown !== ended && isCallPending(shown)) return shown;
      return findPendingCall(deviceRef.current, ended);
    });
  }, []);

  const clearLocalCallUiState = useCallback((): void => {
    callRef.current = null;
    setIsMuted(false);
    setCallDuration(0);
    setIsKeypadOpen(false);
    setKeypadDigits('');
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }
  }, []);

  // Safety net for microphone errors raised by the Twilio SDK itself
  // (31401 permission denied / 31402 acquisition failed): the SDK acquires
  // the mic at connect/accept time, so anything that slips past the pre-call
  // probe lands here. Replaces the generic failure toast with the recovery
  // dialog; returns false for unrelated errors so callers keep their
  // existing handling.
  const handleMicVoiceError = useCallback(
    (err: unknown, context: MicGate['context']): boolean => {
      const issue = classifyMicrophoneError(err);
      if (!issue) return false;
      log('microphone issue detected from voice error', issue);
      releasePendingReservation();
      resetCallState();
      setMicGate({ issue, context });
      dispatch(setVisible(false));
      dispatch(setStatus('idle'));
      return true;
    },
    [dispatch, log, releasePendingReservation, resetCallState],
  );

  const startDismissalCountdown = useCallback((): void => {
    // Prevent multiple timers from running
    if (dismissTimerRef.current) return;
    if (!visible) return;

    setShowDismissCountdown(true);
    setDismissProgress(100);
    const started = Date.now();
    const duration = 3000;
    dismissTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - started;
      const remaining = Math.max(duration - elapsed, 0);
      const percent = (remaining / duration) * 100;
      setDismissProgress(percent);
      if (remaining <= 0) {
        clearDismissTimer();
        setShowDismissCountdown(false);
        setDismissProgress(0);
        dispatch(setStatus('idle'));
        dispatch(setVisible(false));
        dispatch(setCustomer(null));
        dispatch(setCallInfo(null));
      }
    }, 50); // Smoother animation with 50ms intervals
  }, [clearDismissTimer, dispatch, visible]);

  // Reset dismissal countdown when call becomes active; start it when call ends
  useEffect(() => {
    // While a forward is in flight (initiated/ringing), the local
    // Twilio leg's `disconnect` event will fire as soon as Twilio severs the
    // bridge — but the bar should remain to show forward progress until the
    // socket flow finalizes (or 'failed'/'canceled' resolves it).
    if (isForwardingInProgress) {
      setShowDismissCountdown(false);
      setDismissProgress(100);
      clearDismissTimer();
      return;
    }

    if (isForwardingTerminal) return;

    if (status === 'connecting' || status === 'ringing' || status === 'answered') {
      setShowDismissCountdown(false);
      setDismissProgress(100);
      clearDismissTimer();
    } else {
      // Covers 'disconnected', 'idle', 'error' — guard inside startDismissalCountdown
      // prevents spurious fires when the bar is not visible (e.g. initial idle state).
      startDismissalCountdown();
    }
  }, [
    status,
    isForwardingInProgress,
    isForwardingTerminal,
    clearDismissTimer, // Covers 'disconnected', 'idle', 'error' — guard inside startDismissalCountdown
    // prevents spurious fires when the bar is not visible (e.g. initial idle state).
    startDismissalCountdown,
  ]);

  // When forward terminates, dismiss the bar. The local SDK leg has
  // already been severed by Twilio's redirect — we just need to clean up
  // state and start the same dismissal countdown used for ended calls.
  useEffect(() => {
    if (
      forwardingStatus !== 'answered' &&
      forwardingStatus !== 'failed' &&
      forwardingStatus !== 'canceled'
    ) {
      handledForwardingTerminalRef.current = null;
      return;
    }

    if (handledForwardingTerminalRef.current === forwardingStatus) return;
    handledForwardingTerminalRef.current = forwardingStatus;

    const targetName = forwardingTargetUser?.name || 'teammate';
    if (forwardingStatus === 'answered') {
      toast.success(`${targetName} answered`);
    } else if (forwardingStatus === 'failed') {
      toast.error(`Forward to ${targetName} failed`);
    }

    clearLocalCallUiState();
    setIsForwardPanelOpen(false);
    dispatch(setStatus('disconnected'));
    startDismissalCountdown();
  }, [
    forwardingStatus,
    clearLocalCallUiState,
    dispatch,
    forwardingTargetUser?.name,
    startDismissalCountdown,
  ]);

  // Whenever the bar is hidden, ensure the forward panel can't linger.
  useEffect(() => {
    if (!visible) {
      setIsForwardPanelOpen(false);
    }
  }, [visible]);

  // Safety timeout: if call setup (reservation + device.connect) doesn't
  // progress within 30s, force error. Deliberately NOT applied to 'ringing':
  // the Dial uses answerOnBridge, so this leg sits in 'ringing' for as long as
  // the customer's phone rings — up to the server's 60s OUTBOUND_RING_TIMEOUT.
  // A 30s timer here tore down every call the customer took longer than that
  // to answer ("Call failed to connect"), and the abandoned Twilio leg then
  // left the customer lock bound, so the redial was rejected as "already on a
  // call". Twilio owns the ring timeout; the call's disconnect/cancel/error
  // events end the attempt (mirrors the mobile app's setup-only watchdog).
  // Also paused while the microphone probe is unresolved — the user may sit
  // on the browser's native permission prompt for longer than 30s, and that
  // must not be reported as a connection failure.
  useEffect(() => {
    if (callSetupTimeoutRef.current) {
      clearTimeout(callSetupTimeoutRef.current);
      callSetupTimeoutRef.current = null;
    }

    if (status === 'connecting' && !isMicProbePending) {
      callSetupTimeoutRef.current = setTimeout(() => {
        log('call setup timed out from status:', status);
        callAttemptRef.current += 1;
        releasePendingReservation();
        resetCallState();
        toast.error('Call failed to connect. Please try again.');
        dispatch(setStatus('error'));
      }, 30_000);
    }

    return () => {
      if (callSetupTimeoutRef.current) {
        clearTimeout(callSetupTimeoutRef.current);
        callSetupTimeoutRef.current = null;
      }
    };
  }, [status, isMicProbePending, dispatch, log, releasePendingReservation, resetCallState]);

  useEffect(() => {
    return () => {
      clearDismissTimer();
      if (callSetupTimeoutRef.current) {
        clearTimeout(callSetupTimeoutRef.current);
      }
      // cleanup device on unmount
      if (deviceRef.current) {
        try {
          log('destroying device on unmount');
          deviceRef.current.destroy();
        } catch {
          log('error while destroying device (ignored)');
        }
      }
    };
  }, [clearDismissTimer, log]);

  // Initialize device for incoming calls
  useEffect(() => {
    if (!hasVoiceProduct) {
      if (deviceRef.current) {
        try {
          log('destroying device because voice product is unavailable');
          deviceRef.current.destroy();
        } catch {
          log('error while destroying device (ignored)');
        }
        deviceRef.current = null;
        deviceInitializedRef.current = false;
      }
      return;
    }

    // Scoped to this run of the effect, so its cleanup stops everything the
    // run started.
    let cancelled = false;
    let initInFlight = false;
    let initGeneration = 0;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let retryCount = 0;

    const clearRetryTimer = (): void => {
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
    };

    // The reference is dropped before destroy() so the device's own
    // 'unregistered'/'destroyed' listeners can tell this deliberate teardown
    // from the SDK giving up on the device.
    const destroyDevice = (device: Device): void => {
      if (deviceRef.current === device) {
        deviceRef.current = null;
        deviceInitializedRef.current = false;
      }
      try {
        device.destroy();
      } catch {
        log('error while destroying device (ignored)');
      }
    };

    // An unregistered device cannot ring. One that holds a call, or that a
    // call is being placed on, is left alone: the SDK reconnects it by itself
    // and destroying it would hang up.
    const needsReplacing = (device: Device): boolean =>
      device.state === Device.State.Destroyed ||
      (device.state === Device.State.Unregistered &&
        !callRef.current &&
        !initiatedRef.current &&
        !device.isBusy &&
        device.calls.length === 0);

    const scheduleRetry = (): void => {
      if (cancelled || retryTimer) return;
      const delay =
        DEVICE_RETRY_DELAYS_MS[Math.min(retryCount, DEVICE_RETRY_DELAYS_MS.length - 1)];
      retryCount += 1;
      retryTimer = setTimeout(() => {
        retryTimer = null;
        void ensureDevice();
      }, delay);
    };

    // Make sure this tab has a device that can ring: create one if there is
    // none, replace one that has dropped to 'unregistered'. A failed token
    // request or registration used to leave the tab deaf to incoming calls
    // until a reload, so failures are retried with a backoff.
    const ensureDevice = async (): Promise<void> => {
      // deviceInitializedRef is only set once register() resolves, so the
      // in-flight flag is what stops two devices being created side by side.
      if (cancelled || initInFlight) return;
      const existing = deviceRef.current;
      if (existing && !needsReplacing(existing)) {
        // Unregistered but busy: look again once the call is out of the way.
        if (existing.state === Device.State.Unregistered) scheduleRetry();
        return;
      }

      initInFlight = true;
      initGeneration += 1;
      const generation = initGeneration;
      const isSuperseded = (): boolean => cancelled || generation !== initGeneration;
      clearRetryTimer();

      let createdDevice: Device | null = null;
      try {
        log('initializing device for incoming calls');
        const token = await getAccessToken();
        if (isSuperseded()) return;

        // The token request doubles as a reachability check: a device the SDK
        // is still reconnecting is only thrown away once the network is known
        // to be back, and not at all if it recovered in the meantime.
        const stale = deviceRef.current;
        if (stale) {
          if (!needsReplacing(stale)) return;
          log('replacing device that is no longer registered');
          destroyDevice(stale);
        }

        // Refresh a minute ahead rather than the SDK's 10s default: one slow
        // token request (or a throttled background tab) inside that window
        // let the token lapse, which unregisters the device so incoming calls
        // silently stop ringing in this tab.
        const device = new Device(token, {
          logLevel: 'error',
          tokenRefreshMs: TOKEN_REFRESH_LEAD_MS,
        });
        createdDevice = device;

        // Swap in a fresh token. Resolves false when it could not be fetched.
        const refreshDeviceToken = async (): Promise<boolean> => {
          try {
            const newToken = await getAccessToken();
            if (device.state === Device.State.Destroyed) return false;
            device.updateToken(newToken);
            if (device.state === Device.State.Unregistered) {
              await device.register();
            }
            return true;
          } catch (err) {
            log('failed to refresh token', err);
            return false;
          }
        };

        // Start over with a new Device. After an expired-token error the SDK
        // stops its registration heartbeat yet still reports 'registered', so
        // swapping the token alone would leave this tab deaf to incoming
        // calls while looking healthy.
        const rebuildDevice = (): void => {
          deviceRebuildPendingRef.current = false;
          log('rebuilding device with a fresh token');
          destroyDevice(device);
          // An attempt still waiting on this device's register() never
          // settles once the device is destroyed; let the new attempt through.
          initGeneration += 1;
          initInFlight = false;
          void ensureDevice();
        };
        rebuildDeviceRef.current = rebuildDevice;

        device.on('registering', () => log('device registering'));
        device.on('registered', () => {
          log('device registered');
          if (deviceRef.current !== device) return;
          clearRetryTimer();
          retryCount = 0;
          signalingDownSinceRef.current = null;
          signalingDownWarnedRef.current = false;
        });
        device.on('unregistered', () => {
          log('device unregistered');
          // The SDK normally re-registers by itself once its socket is back.
          // Check that it did, so a device that stays unregistered is replaced
          // rather than left silently unable to ring.
          if (deviceRef.current === device) scheduleRetry();
        });
        device.on('destroyed', () => {
          // Teardowns started here drop the reference first, so this is the
          // SDK destroying the device on its own (it does so on pagehide).
          if (deviceRef.current !== device) return;
          log('device destroyed event; clearing reference');
          deviceRef.current = null;
          deviceInitializedRef.current = false;
          scheduleRetry();
        });
        device.on('tokenWillExpire', async () => {
          log('token will expire, refreshing');
          if (await refreshDeviceToken()) return;
          // One retry inside the refresh window before the token lapses.
          setTimeout(() => {
            void refreshDeviceToken();
          }, TOKEN_REFRESH_RETRY_MS);
        });
        device.on('error', (err: DeviceError) => {
          log('device error', err);
          if (handleMicVoiceError(err, null)) return;

          // An expired or rejected token only affects registration — a call
          // already in progress keeps its media, so never hang up for it. The
          // throttle stops a token that keeps failing from looping; a repeat
          // falls through to the visible error below.
          if (
            DEVICE_TOKEN_ERROR_CODES.has(err?.code ?? 0) &&
            Date.now() - lastTokenRecoveryRef.current > TOKEN_RECOVERY_THROTTLE_MS
          ) {
            lastTokenRecoveryRef.current = Date.now();
            if (callRef.current || device.isBusy || device.calls.length > 0) {
              log('token error during a call; refreshing token, rebuilding device afterwards');
              deviceRebuildPendingRef.current = true;
              void refreshDeviceToken();
            } else {
              rebuildDevice();
            }
            return;
          }

          // A signalling socket that drops while nothing is happening needs
          // nothing from the user: the SDK reconnects and re-registers by
          // itself, and the 'unregistered' check above replaces the device if
          // it does not. Telling an idle agent to refresh for every network
          // blip was noise, and left the status on 'error' for no reason.
          if (
            isTransientSignalingError(err) &&
            !callRef.current &&
            !initiatedRef.current &&
            !pendingReservationRef.current &&
            !device.isBusy &&
            device.calls.length === 0
          ) {
            log('signalling connection dropped while idle; leaving the SDK to reconnect');
            // A blip heals itself, but a connection that stays down (a
            // firewall or VPN blocking the socket) means this tab cannot
            // ring. Say so once rather than on every retry.
            signalingDownSinceRef.current ??= Date.now();
            if (
              !signalingDownWarnedRef.current &&
              Date.now() - signalingDownSinceRef.current > SIGNALING_DOWN_WARNING_MS
            ) {
              signalingDownWarnedRef.current = true;
              toast.warning(
                "Can't reach the phone service, so calls won't ring in this tab. Still retrying.",
              );
            }
            return;
          }

          // Device errors never name the call they affect. An established
          // call survives registration and signalling errors (the SDK
          // reconnects it), and tearing it down here hung up healthy calls.
          // A call that is still being set up cannot reconnect: the SDK closes
          // it without telling the call's own listeners, so end the attempt.
          const liveCallState = callRef.current?.status() as string | undefined;
          if (liveCallState === 'open' || liveCallState === 'reconnecting') {
            log('device error during an established call; leaving the call up');
            return;
          }

          releasePendingReservation();
          resetCallState();
          toast.error('Voice connection error. Please refresh and try again.');
          dispatch(setStatus('error'));
        });

        // Handle incoming calls
        device.on('incoming', (call: Call) => {
          log('incoming call received', {
            from: call.parameters.From,
            sid: call.parameters.CallSid,
          });
          // A caller already on screen stays there; this one rings behind it
          // in device.calls and is shown when the first has gone.
          refreshIncomingCall();

          // Once accepted, the listeners attached on accept own the call.
          const isAcceptedHere = (): boolean => callRef.current === call;

          // Cancelled: answered by another agent, or the caller hung up.
          call.on('cancel', () => {
            log('incoming call cancelled');
            if (isAcceptedHere()) return;
            refreshIncomingCall(call);
            // Leave the bar alone when it belongs to another call that is
            // live or being placed in this tab.
            if (!callRef.current && !initiatedRef.current) {
              dispatch(setVisible(false));
            }
          });
          call.on('reject', () => {
            log('incoming call rejected');
            if (isAcceptedHere()) return;
            refreshIncomingCall(call);
          });
          // The SDK drops an unanswered call when its signalling socket closes
          // and raises no 'cancel' for it. Its status still reads 'pending'
          // while this event is delivered.
          call.on('transportClose', () => {
            if (isAcceptedHere() || !isCallPending(call)) return;
            log('incoming call lost with the signalling connection');
            refreshIncomingCall(call);
          });
        });

        deviceRef.current = device;

        // Register the device to receive incoming calls
        await device.register();
        if (isSuperseded()) return;

        deviceInitializedRef.current = true;
        retryCount = 0;
        log('device registered and ready for incoming calls');
      } catch (err) {
        log('failed to initialize device', err);
        // A device that never registered must not linger: it would hold a
        // socket open and block the next attempt.
        if (createdDevice) destroyDevice(createdDevice);
        if (isSuperseded()) return;
        if (isTokenRequestRefused(err)) {
          log('voice token request was refused; not retrying on a timer');
          return;
        }
        scheduleRetry();
      } finally {
        if (generation === initGeneration) initInFlight = false;
      }
    };

    // Coming back online or back to the tab are the moments a dead device
    // gets noticed, so do not wait out the backoff then.
    const handleOnline = (): void => {
      void ensureDevice();
    };
    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') void ensureDevice();
    };
    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    void ensureDevice();

    return () => {
      cancelled = true;
      clearRetryTimer();
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // An attempt still waiting on register() would otherwise leave its
      // half-built device behind for the next run to trip over.
      const device = deviceRef.current;
      if (device && !deviceInitializedRef.current) destroyDevice(device);
    };
  }, [
    hasVoiceProduct,
    dispatch,
    getAccessToken,
    handleMicVoiceError,
    log,
    refreshIncomingCall,
    releasePendingReservation,
    resetCallState,
  ]);

  // A token error during a call defers the device rebuild until this tab is
  // off the phone again.
  useEffect(() => {
    if (!deviceRebuildPendingRef.current || callRef.current || incomingCall) return;
    if (status === 'idle' || status === 'disconnected' || status === 'error') {
      rebuildDeviceRef.current?.();
    }
  }, [status, incomingCall]);

  const handleAcceptIncoming = async (): Promise<void> => {
    const call = incomingCall;
    if (!call || acceptingCallRef.current) return;
    acceptingCallRef.current = call;
    setIsAcceptingIncoming(true);

    // Verify microphone access before accepting — Twilio acquires the mic
    // during accept(), and a blocked mic would kill the call with an opaque
    // error. The call keeps ringing while the recovery dialog is open, so a
    // retry can still pick it up.
    setIsMicProbePending(true);
    let micResult: MicrophoneAccessResult;
    let wasDeclined = false;
    try {
      micResult = await probeMicrophoneAccess();
    } finally {
      // After a decline the slot may already belong to an Accept on the next
      // caller, so only release it when it is still this call's.
      wasDeclined = acceptingCallRef.current !== call;
      if (!wasDeclined) {
        acceptingCallRef.current = null;
        setIsAcceptingIncoming(false);
      }
      setIsMicProbePending(false);
    }

    // Declined while the microphone check was waiting.
    if (wasDeclined) return;

    if (!micResult.ok) {
      log('microphone check failed for incoming call', micResult.issue);
      setMicGate({ issue: micResult.issue, context: 'incoming' });
      return;
    }

    // The check can sit on the browser's permission prompt for longer than
    // the call rings. accept() on a call that has gone does nothing at all,
    // so going ahead would leave the bar on "Connecting..." until the setup
    // watchdog reported a failure that never happened.
    if (!isCallPending(call)) {
      log('incoming call ended during the microphone check');
      refreshIncomingCall(call);
      toast.info('That call was answered elsewhere or the caller hung up.');
      return;
    }

    try {
      log('accepting incoming call');

      const from = (call as TwilioCallWithParams)?.parameters?.From;
      const sid = (call as TwilioCallWithParams)?.parameters?.CallSid;

      // Extract customer info from TwiML params
      const customParams = (call as TwilioCallWithParams)?.customParameters;
      const incomingCustomer = customParams?.size
        ? {
            _id: customParams.get('_id') || '',
            name: customParams.get('name') || '',
            phone: customParams.get('phone') || from || '',
            email: customParams.get('email') || '',
            isBlocked: customParams.get('isBlocked') === 'true',
          }
        : null;

      // Mark as initiated to prevent placeCall from being triggered
      initiatedRef.current = true;

      // Simply accept the incoming call
      call.accept();

      callRef.current = call;
      // Another caller may still be ringing; they take the dialog's slot and
      // are shown again once this call's bar has gone.
      refreshIncomingCall(call);

      log('incoming call accepted', { sid, from });

      dispatch(setCallInfo({ callSid: sid }));
      // Set customer info from TwiML params immediately (will be updated when backend syncs)
      if (incomingCustomer) {
        dispatch(setCustomer(incomingCustomer));
      }
      dispatch(setStatus('connecting'));
      dispatch(setVisible(true));

      // Initialize mute state
      try {
        setIsMuted(call.isMuted());
      } catch {
        setIsMuted(false);
      }

      // Set up call event listeners
      call.on('accept', () => {
        log('call event: accept');
        dispatch(setStatus('answered'));

        // Start call timer
        setCallDuration(0);
        if (callTimerRef.current) {
          clearInterval(callTimerRef.current);
        }
        callTimerRef.current = setInterval(() => {
          setCallDuration((prev) => prev + 1);
        }, 1000);
      });

      call.on('disconnect', () => {
        log('call event: disconnect');
        resetCallState();
        dispatch(setStatus('disconnected'));
        startDismissalCountdown();
      });

      // The caller can still hang up, or another agent win the call, in the
      // moment between accept() and the media connecting. The SDK reports
      // that as 'cancel' and raises no 'disconnect', so without this the bar
      // stayed on "Connecting..." until the setup watchdog fired.
      call.on('cancel', () => {
        log('call event: cancel');
        if (callRef.current !== call) return;
        resetCallState();
        toast.info('That call was answered elsewhere or the caller hung up.');
        dispatch(setStatus('disconnected'));
        startDismissalCountdown();
      });

      call.on('reject', () => {
        log('call event: reject');
        if (callRef.current !== call) return;
        resetCallState();
        dispatch(setStatus('disconnected'));
        startDismissalCountdown();
      });

      call.on('mute', (muted: boolean) => {
        log('call event: mute', muted);
        setIsMuted(muted);
      });

      call.on('error', (err: Error & { code?: number }) => {
        log('call event: error', err);
        if (handleMicVoiceError(err, 'incoming')) return;
        resetCallState();
        toast.error('Call error occurred. Please try again.');
        dispatch(setStatus('error'));
      });
    } catch (err) {
      log('failed to accept incoming call', err);
      if (handleMicVoiceError(err, 'incoming')) {
        refreshIncomingCall(call);
        return;
      }
      resetCallState();
      toast.error('Failed to accept call. Please try again.');
      dispatch(setStatus('error'));
      refreshIncomingCall(call);
    }
  };

  const handleDeclineIncoming = (): void => {
    if (!incomingCall) return;

    // Withdraw an Accept that is still waiting on the microphone check.
    if (acceptingCallRef.current === incomingCall) {
      acceptingCallRef.current = null;
      setIsAcceptingIncoming(false);
    }

    try {
      log('declining incoming call');
      incomingCall.reject();
      incomingCall.disconnect();
    } catch (err) {
      log('failed to decline incoming call', err);
    }
    refreshIncomingCall(incomingCall);
  };

  // Retry from the microphone recovery dialog: re-probe and, if access is now
  // granted, resume the blocked call (redial for outbound, accept for a
  // still-ringing incoming call).
  const handleMicRetry = async (): Promise<void> => {
    if (!micGate) return;
    const { context } = micGate;

    setIsMicRetrying(true);
    let result: MicrophoneAccessResult;
    try {
      result = await probeMicrophoneAccess();
    } finally {
      setIsMicRetrying(false);
    }

    if (!result.ok) {
      // Still blocked — keep the dialog open (the issue kind may change,
      // e.g. permission granted but no microphone plugged in).
      setMicGate({ issue: result.issue, context });
      return;
    }

    setMicGate(null);

    if (context === 'incoming') {
      if (incomingCall) {
        void handleAcceptIncoming();
      } else {
        toast.error('The incoming call already ended.');
      }
      return;
    }

    if (context === 'outbound' && customer?.phone) {
      // Redux still holds the pending customer; re-enter the normal outbound
      // initiation path (the initiation effect dials once status is
      // 'connecting' and the bar is visible).
      dispatch(setStatus('connecting'));
      dispatch(setVisible(true));
    }
  };

  const handleMicCancel = (): void => {
    if (!micGate) return;
    const { context } = micGate;
    setMicGate(null);
    if (context === 'outbound') {
      // Drop the pending call intent entirely.
      dispatch(setCustomer(null));
      dispatch(setCallInfo(null));
    }
    // Incoming: just dismiss — the ringing IncomingCallDialog reappears so
    // the user can still decline (or let the call route onward).
  };

  const handleEndCall = async (): Promise<void> => {
    if (isForwardingActive) return;

    callAttemptRef.current += 1;
    releasePendingReservation();
    resetCallState();
    dispatch(setStatus('disconnected'));
    startDismissalCountdown();
  };

  const handleBlockCustomer = async (): Promise<void> => {
    if (!auth?.user?._temporaryBusiness) {
      toast.error('You are not authorized to block customers');
      return;
    }

    try {
      const res = await axios.post('/api/block-customer', {
        _customer: customer._id,
        isBlocked: true,
        _business: auth.user._temporaryBusiness,
      });

      if (res.data.error) {
        toast.error('Failed to block customer');
        return;
      }

      toast.success('Customer blocked successfully');

      // Update customer state with blocked status
      const updatedCustomer = res.data.customer;
      dispatch(setCustomer(updatedCustomer));

      // End the call immediately after blocking
      await handleEndCall();
    } catch (err) {
      log('failed to block customer', err);
    } finally {
      setShowBlockDialog(false);
    }
  };

  const toggleMute = (): void => {
    if (!canUseAudioControls) return;

    const call = callRef.current;
    if (!call) return;
    const next = !isMuted;
    try {
      call.mute(next);
    } catch (err) {
      log('failed to toggle mute', err);
    } finally {
      setIsMuted(next);
    }
  };

  const canUseAudioControls =
    !isForwardingActive &&
    (status === 'answered' || status === 'ringing' || status === 'connecting');
  const canUseKeypad = !isForwardingActive && status === 'answered';

  const sendDtmfDigit = (digit: string): void => {
    if (!canUseKeypad) return;
    const call = callRef.current as unknown as {
      sendDigits?: (d: string) => void;
    };
    if (!call?.sendDigits) return;

    try {
      call.sendDigits(digit);
    } catch (err) {
      log('failed to send DTMF digit', err);
    }
  };

  const handlePressKeypadDigit = (digit: string): void => {
    if (!digit) return;
    setKeypadDigits((prev) => {
      if (prev.length >= 12) return prev;
      return `${prev}${digit}`;
    });
    sendDtmfDigit(digit);
  };

  const closeKeypad = (): void => {
    setIsKeypadOpen(false);
  };

  const formatCallDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getInitials = (name: string): string => {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const getStatusConfig = (
    currentStatus: string,
  ): { text: string; color: string; dotColor: string } => {
    const targetName = forwardingTargetUser?.name || 'teammate';
    if (forwardingStatus === 'initiated') {
      return {
        text: `Calling ${targetName}…`,
        color: 'text-yellow-400',
        dotColor: 'bg-yellow-400',
      };
    }
    if (forwardingStatus === 'ringing') {
      return {
        text: `Ringing ${targetName}…`,
        color: 'text-blue-400',
        dotColor: 'bg-blue-400',
      };
    }
    if (forwardingStatus === 'answered') {
      return {
        text: `${targetName} answered`,
        color: 'text-green-400',
        dotColor: 'bg-green-400',
      };
    }
    if (forwardingStatus === 'failed') {
      return {
        text: `Forward failed`,
        color: 'text-red-400',
        dotColor: 'bg-red-400',
      };
    }
    if (forwardingStatus === 'canceled') {
      return {
        text: 'Forward canceled',
        color: 'text-gray-400',
        dotColor: 'bg-gray-400',
      };
    }

    switch (currentStatus) {
      case 'connecting':
        return {
          // While the getUserMedia probe is unresolved the browser may be
          // showing its native permission prompt — tell the user what to do.
          text: isMicProbePending ? 'Allow microphone to continue' : 'Connecting...',
          color: 'text-yellow-400',
          dotColor: 'bg-yellow-400',
        };
      case 'ringing':
        if (hasCustomerAnsweredBeforeBridge) {
          return {
            text: 'Answered',
            color: 'text-green-400',
            dotColor: 'bg-green-400',
          };
        }
        return {
          text: 'Ringing...',
          color: 'text-blue-400',
          dotColor: 'bg-blue-400',
        };
      case 'answered':
        return {
          text: 'Connected',
          color: 'text-green-400',
          dotColor: 'bg-green-400',
        };
      case 'error':
        return {
          text: 'Call Failed',
          color: 'text-red-400',
          dotColor: 'bg-red-400',
        };
      default:
        return {
          text: 'Ended',
          color: 'text-gray-400',
          dotColor: 'bg-gray-400',
        };
    }
  };

  const placeCall = useCallback(async (): Promise<void> => {
    if (!customer?.phone) return;
    if (!deviceRef.current) {
      log('device not ready for outbound call');
      toast.error('Voice device is not ready. Please refresh and try again.');
      dispatch(setStatus('error'));
      return;
    }

    let reservationToken: string | null = null;
    const attemptId = callAttemptRef.current;
    try {
      log('placeCall invoked', {
        customer: customer?._id,
        phone: customer?.phone,
      });
      dispatch(setStatus('connecting'));

      if (isCustomerOnActiveCall(ongoingCalls, customer)) {
        toast.error('This customer is already on a call with another teammate.');
        dispatch(setStatus('error'));
        return;
      }

      // Verify microphone access before reserving the call so a blocked mic
      // can't create a reservation or ring the customer (Twilio only requests
      // the mic at connect time, which is too late to explain the problem).
      // May show the browser's native permission prompt.
      setIsMicProbePending(true);
      let micResult: MicrophoneAccessResult;
      try {
        micResult = await probeMicrophoneAccess();
      } finally {
        setIsMicProbePending(false);
      }

      if (callAttemptRef.current !== attemptId) {
        // The user ended this attempt while the permission prompt was open.
        log('call attempt canceled during microphone check');
        return;
      }

      if (!micResult.ok) {
        log('microphone check failed', micResult.issue);
        setMicGate({ issue: micResult.issue, context: 'outbound' });
        dispatch(setVisible(false));
        dispatch(setStatus('idle'));
        return;
      }

      // Outlive the 15s axios default: this endpoint reconciles ongoing calls
      // against Twilio and can be slow under load. Timing out while the server
      // is still granting strands a reservation that blocks redials.
      const reservationResponse = await axios.post(
        '/api/voice/outbound-reservation',
        {
          _customer: customer?._id,
          phone: customer.phone,
        },
        { timeout: 30000 },
      );
      reservationToken = reservationResponse.data?.reservationToken;
      if (!reservationToken) {
        throw new Error('Outbound call reservation was not created');
      }
      pendingReservationRef.current = { phone: customer.phone, reservationToken };

      if (callAttemptRef.current !== attemptId) {
        // The user ended this attempt while the reservation was in flight.
        // Without this check the call would still be placed, ringing the
        // customer with no call UI attached.
        log('call attempt canceled during reservation; releasing');
        releasePendingReservation();
        return;
      }

      log('calling device.connect', { to: customer.phone });
      const call = await deviceRef.current.connect({
        params: {
          To: customer.phone,
          ReservationToken: reservationToken,
        },
      });
      log('device.connect resolved');
      // Placing a call makes the SDK ignore any caller still ringing, without
      // an event, so drop a dialog that can no longer be answered.
      refreshIncomingCall();

      if (callAttemptRef.current !== attemptId) {
        // The attempt was torn down (End pressed, or the setup watchdog
        // fired) while connect was in flight. Nothing owns this call any
        // more, so it must not go on to ring the customer.
        log('call attempt canceled during connect; hanging up');
        try {
          call.disconnect();
        } catch {
          // call may already be disconnected
        }
        releasePendingReservation();
        return;
      }

      callRef.current = call;

      // Initialize mute state
      try {
        setIsMuted(call.isMuted());
      } catch {
        setIsMuted(false);
      }

      const sid = (call as unknown as { parameters?: { CallSid?: string } })?.parameters?.CallSid;

      log('call created', { sid });

      dispatch(setCallInfo({ callSid: sid }));
      dispatch(setStatus('ringing'));

      call.on('accept', () => {
        log('call event: accept');
        // Accepted means Twilio ran the outbound TwiML, which consumed the
        // reservation — nothing left to release.
        pendingReservationRef.current = null;
        dispatch(setStatus('answered'));
        // Start call timer
        setCallDuration(0);
        if (callTimerRef.current) {
          clearInterval(callTimerRef.current);
        }
        callTimerRef.current = setInterval(() => {
          setCallDuration((prev) => prev + 1);
        }, 1000);
      });
      call.on('disconnect', () => {
        log('call event: disconnect');
        releasePendingReservation();
        if (
          forwardingStatusRef.current === 'initiated' ||
          forwardingStatusRef.current === 'ringing'
        ) {
          clearLocalCallUiState();
          dispatch(setForwardingStatus('answered'));
          return;
        }
        resetCallState();
        dispatch(setStatus('disconnected'));
        startDismissalCountdown();
      });
      call.on('cancel', () => {
        log('call event: cancel');
        releasePendingReservation();
        if (
          forwardingStatusRef.current === 'initiated' ||
          forwardingStatusRef.current === 'ringing'
        ) {
          clearLocalCallUiState();
          dispatch(setForwardingStatus('answered'));
          return;
        }
        resetCallState();
        dispatch(setStatus('disconnected'));
        startDismissalCountdown();
      });
      call.on('reject', () => {
        log('call event: reject');
        releasePendingReservation();
        if (
          forwardingStatusRef.current === 'initiated' ||
          forwardingStatusRef.current === 'ringing'
        ) {
          clearLocalCallUiState();
          dispatch(setForwardingStatus('answered'));
          return;
        }
        resetCallState();
        dispatch(setStatus('disconnected'));
        startDismissalCountdown();
      });
      call.on('mute', (muted: boolean) => {
        log('call event: mute', muted);
        setIsMuted(muted);
      });
      call.on('error', (err: Error & { code?: number }) => {
        log('call event: error', err);
        if (handleMicVoiceError(err, 'outbound')) return;
        releasePendingReservation();
        resetCallState();
        if (err.code === 31005) {
          toast.error(
            'Voice calling is not properly configured. Please contact your administrator.',
          );
        } else {
          toast.error('Call error occurred. Please try again.');
        }
        dispatch(setStatus('error'));
      });
    } catch (err) {
      log('placeCall failed', err);
      if (handleMicVoiceError(err, 'outbound')) return;
      releasePendingReservation();
      resetCallState();
      if (
        axios.isAxiosError(err) &&
        err.response?.data?.code === 'CUSTOMER_CALL_IN_PROGRESS'
      ) {
        toast.error(
          err.response.data.message ||
            'This customer is already on a call with another teammate.',
        );
      } else if (
        axios.isAxiosError(err) &&
        err.response?.data?.code === 'CALL_AVAILABILITY_CHECK_FAILED'
      ) {
        toast.error(err.response.data.message);
      } else {
        toast.error('Call failed to connect. Please try again.');
      }
      dispatch(setStatus('error'));
    }
  }, [
    clearLocalCallUiState,
    customer,
    dispatch,
    handleMicVoiceError,
    log,
    ongoingCalls,
    refreshIncomingCall,
    releasePendingReservation,
    resetCallState,
    startDismissalCountdown,
  ]);

  // Every outbound initiator dispatches setStatus('connecting') alongside
  // setCustomer/setVisible, so gating on it stops this effect from re-dialing
  // a finished call when initiatedRef is reset below while the bar is still
  // visible (e.g. during the post-call dismissal countdown).
  useEffect(() => {
    if (hasVoiceProduct && visible && customer && status === 'connecting' && !initiatedRef.current) {
      initiatedRef.current = true;
      placeCall();
    }
    if (!visible || !hasVoiceProduct) {
      initiatedRef.current = false;
    }
  }, [hasVoiceProduct, visible, customer?._id, customer, status, placeCall]);

  // Once an attempt reaches a terminal state, allow the next initiation even
  // while the bar is still visible — otherwise a redial started during the
  // dismissal countdown is silently swallowed and the bar sits on
  // "Connecting..." until the setup timeout.
  useEffect(() => {
    if (status === 'disconnected' || status === 'error' || status === 'idle') {
      initiatedRef.current = false;
    }
  }, [status]);

  // Simple conversation lookup by customer ID
  const customerConversation = useMemo(() => {
    if (!customer?._id || !customerConversations?.length) return undefined;
    return customerConversations.find((conv) => {
      const convCustomer = conv?._customer;
      if (typeof convCustomer === 'object' && convCustomer?._id === customer._id) {
        return true;
      }
      if (typeof convCustomer === 'string' && convCustomer === customer._id) {
        return true;
      }
      return false;
    });
  }, [customer?._id, customerConversations]);

  const conversationId = customerConversation?._id;

  // Once the user has started or accepted a call its bar takes the screen. A
  // caller still ringing at that point stays in incomingCall and gets the
  // dialog back when the bar has gone (if they are still ringing).
  const hasActiveCallBar =
    visible && (status === 'connecting' || status === 'ringing' || status === 'answered');
  const shownIncomingCall = hasActiveCallBar ? null : incomingCall;

  const incomingFrom = (shownIncomingCall as TwilioCallWithParams)?.parameters?.From;

  const displayName =
    customer?.name || (incomingFrom ? formatPhone(incomingFrom) : 'Unknown Contact');
  const displayPhone = customer?.phone || incomingFrom || '';

  const handleViewDetails = (): void => {
    if (!conversationId) return;
    dispatch(setActiveConversation(conversationId));
    navigate(
      buildCanonicalCustomerConversationPath({
        _customerConversation: conversationId,
        _business: customerConversation?._business,
      }),
    );
  };

  // Extract customer data from TwiML custom parameters passed by backend
  const inboundCallCustomer = useMemo(() => {
    if (!shownIncomingCall) return null;

    // Access custom parameters passed via TwiML from the backend
    const customParams = (shownIncomingCall as TwilioCallWithParams)?.customParameters;

    if (!customParams || customParams.size === 0) return null;

    // Return lean customer object matching front-end format
    return {
      _id: customParams.get('_id') || '',
      name: customParams.get('name') || '',
      phone: customParams.get('phone') || '',
      email: customParams.get('email') || '',
      isBlocked: customParams.get('isBlocked') === 'true',
    };
  }, [shownIncomingCall]);

  // Alert the agent via browser notification and title blink
  useIncomingCallAlerts({
    active: hasVoiceProduct && !!shownIncomingCall,
    callerName: inboundCallCustomer?.name,
  });

  if (!hasVoiceProduct) {
    return null;
  }

  // Rendered in every branch below (closed while micGate is null) so the
  // recovery flow survives whichever call surface is on screen. Radix portals
  // it to document.body, so placement here does not affect layout.
  const microphonePermissionDialog = (
    <MicrophonePermissionDialog
      issue={micGate?.issue ?? null}
      isRetrying={isMicRetrying}
      cancelLabel={micGate?.context === 'incoming' ? 'Dismiss' : 'Cancel call'}
      onRetry={() => {
        void handleMicRetry();
      }}
      onCancel={handleMicCancel}
    />
  );

  // Render incoming call dialog if there's an incoming call
  if (shownIncomingCall) {
    return (
      <>
        {/* Hide the ringing card while the mic dialog explains the problem;
            it reappears on dismiss so the user can still decline. */}
        {!micGate && (
          <IncomingCallDialog
            incomingCall={shownIncomingCall}
            isAccepting={isAcceptingIncoming}
            onAccept={handleAcceptIncoming}
            onDecline={handleDeclineIncoming}
            customerName={inboundCallCustomer?.name}
            compact={isCompactCallUi}
          />
        )}
        {microphonePermissionDialog}
      </>
    );
  }

  // Only show active call UI when visible
  if (!visible) {
    return (
      <>
        {microphonePermissionDialog}
      </>
    );
  }

  const statusConfig = getStatusConfig(status);
  const initials = getInitials(displayName);
  const canEndCall =
    !isForwardingActive &&
    (status === 'connecting' || status === 'ringing' || status === 'answered');
  const endCallButtonText = isForwardingInProgress
    ? 'Transferring'
    : forwardingStatus === 'answered'
      ? 'Transferred'
      : canEndCall
        ? 'End Call'
        : 'Ended';

  const blockDialog = (
    <AlertDialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
      <AlertDialogContent onClick={(e) => e.stopPropagation()}>
        <AlertDialogHeader>
          <AlertDialogTitle>Block Customer?</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to block {displayName}? They will no longer be able to
            contact you via phone or messages. The current call will be ended immediately.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={(e) => e.stopPropagation()}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.stopPropagation();
              handleBlockCustomer();
            }}
            className="bg-red-600 hover:bg-red-700"
          >
            Block & End Call
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  const forwardCallPanel = (
    <ForwardCallPanel
      isOpen={isForwardPanelOpen}
      onClose={() => setIsForwardPanelOpen(false)}
      callSid={callInfo?.callSid || null}
      onForwardInitiated={(target) => {
        dispatch(setForwardingTargetUser(target));
        dispatch(setForwardingStatus('initiated'));
      }}
    />
  );

  if (isCompactCallUi) {
    const callTone: CallTone = isForwardingInProgress ? 'forwarding' : canEndCall ? 'live' : 'ended';
    return (
      <>
        <PhoneCallScreen
          expanded={isCallScreenExpanded}
          onExpandedChange={setIsCallScreenExpanded}
          displayName={displayName}
          displayPhone={displayPhone ? formatPhone(displayPhone) : undefined}
          initials={initials}
          statusText={statusConfig.text}
          statusTextClassName={statusConfig.color}
          statusDotClassName={statusConfig.dotColor}
          barStatus={
            // Short, so the caller's name keeps its room in the bar; the
            // call screen has the full forwarding status.
            isForwardingInProgress
              ? 'Forwarding…'
              : forwardingStatus === 'answered'
                ? 'Transferred'
                : status === 'answered' && !isForwardingActive
                  ? formatCallDuration(callDuration)
                  : statusConfig.text
          }
          duration={formatCallDuration(callDuration)}
          tone={callTone}
          isCustomerSpeaking={isCustomerSpeaking}
          note={
            hasCustomerAnsweredBeforeBridge
              ? "The customer picked up. With call recording on, they hear the notice first, then you're connected."
              : undefined
          }
          dismissProgress={showDismissCountdown && !canEndCall ? dismissProgress : null}
          isMuted={isMuted}
          canMute={canUseAudioControls}
          onToggleMute={toggleMute}
          canUseKeypad={canUseKeypad}
          isKeypadOpen={isKeypadOpen}
          keypadDigits={keypadDigits}
          onOpenKeypad={() => setIsKeypadOpen(true)}
          onCloseKeypad={closeKeypad}
          onPressDigit={handlePressKeypadDigit}
          canForward={status === 'answered' && !isForwardingActive}
          isForwardPanelOpen={isForwardPanelOpen}
          onForward={() => setIsForwardPanelOpen(true)}
          onViewConversation={
            conversationId
              ? () => {
                  setIsCallScreenExpanded(false);
                  handleViewDetails();
                }
              : undefined
          }
          onBlock={customer?._id ? () => setShowBlockDialog(true) : undefined}
          canEndCall={canEndCall}
          endCallLabel={endCallButtonText}
          onEndCall={() => {
            void handleEndCall();
          }}
          isOverlayOpen={isForwardPanelOpen || showBlockDialog}
        />
        {blockDialog}
        {forwardCallPanel}
        {microphonePermissionDialog}
      </>
    );
  }

  return (
    <div
      className="fixed z-[1000]"
      style={{
        top: `${position.topPercent}%`,
        right: `${position.rightPercent}%`,
      }}
    >
      <div
        role="status"
        aria-live="polite"
        onMouseDown={startDrag}
        onTouchStart={startDrag}
        className={cn(
          'relative flex flex-col items-center bg-gray-950 text-white border border-gray-800 rounded-2xl shadow-2xl p-5 w-[280px] shadow-black/50 cursor-move active:cursor-grabbing select-none overflow-hidden',
          isKeypadOpen && 'min-h-[420px]',
        )}
      >
        {/* PROGRESS BAR UPDATED */}
        {showDismissCountdown && (
          <div className="absolute top-0 left-0 right-0 h-1 z-10 bg-emerald-900/20">
            <div
              className="h-full bg-emerald-500 transition-all duration-50 ease-linear"
              style={{ width: `${dismissProgress}%` }}
            />
          </div>
        )}

        {/* Header with avatar, name, phone, and menu */}
        <div className="flex items-start justify-between w-full mb-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            {/* Avatar with active speaker indicator */}
            <div className="relative flex-shrink-0">
              <div
                className={cn(
                  'flex items-center justify-center w-11 h-11 rounded-full bg-gray-800 border-2 transition-all duration-300',
                  isCustomerSpeaking
                    ? 'border-green-500 shadow-lg shadow-green-500/50'
                    : 'border-gray-700',
                )}
              >
                <span className="text-sm font-semibold text-white">{initials}</span>
              </div>
              {/* Animated ring when customer is speaking */}
              {isCustomerSpeaking && (
                <div className="absolute inset-0 rounded-full border-2 border-green-400 animate-ping opacity-75" />
              )}
            </div>
            {/* Name and Phone */}
            <div className="flex flex-col justify-center min-w-0 flex-1 gap-0.5">
              <h3 className="text-sm font-semibold text-white truncate">{displayName}</h3>
              {displayPhone && (
                <p className="text-xs text-gray-400 truncate">{formatPhone(displayPhone)}</p>
              )}
              {conversationId && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewDetails();
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  className="w-fit text-xs text-blue-400 hover:text-blue-300 hover:underline underline-offset-4"
                >
                  View Details →
                </button>
              )}
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                aria-label="More options"
                className="h-9 w-9 rounded-full text-gray-300 hover:text-white hover:bg-gray-800/60"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              className="z-[1100] bg-gray-950 border-gray-800 text-white shadow-2xl shadow-black/50"
            >
              {customer?._id && (
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    setShowBlockDialog(true);
                  }}
                  className="text-red-400 focus:text-red-200 focus:bg-red-500/10"
                >
                  <ShieldOff className="h-4 w-4" />
                  Block Caller
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Status and Timer Card */}
        <div
          className={cn(
            'flex items-center justify-between w-full bg-gray-900/50 border border-gray-800 rounded-xl px-4 py-2.5',
            hasCustomerAnsweredBeforeBridge ? 'mb-2' : 'mb-4',
          )}
        >
          <div className="flex items-center gap-2">
            {/* Status Dot */}
            <div
              className={`w-2 h-2 rounded-full ${statusConfig.dotColor} shadow-lg`}
              style={{
                boxShadow: `0 0 8px ${
                  statusConfig.dotColor.includes('yellow')
                    ? 'rgba(251, 191, 36, 0.6)'
                    : statusConfig.dotColor.includes('blue')
                      ? 'rgba(96, 165, 250, 0.6)'
                      : statusConfig.dotColor.includes('green')
                        ? 'rgba(74, 222, 128, 0.6)'
                        : 'rgba(248, 113, 113, 0.6)'
                }`,
              }}
            />
            {/* Status Text */}
            <span className={`text-sm font-medium ${statusConfig.color}`}>{statusConfig.text}</span>
          </div>
          {/* Call Duration */}
          <span className="text-2xl font-bold text-white tabular-nums">
            {formatCallDuration(callDuration)}
          </span>
        </div>
        {hasCustomerAnsweredBeforeBridge && (
          <p className="w-full mb-4 text-xs text-gray-400">
            The customer picked up. With call recording on, they hear the notice first, then
            you're connected.
          </p>
        )}

        {/* Action Buttons */}
        <div className="flex w-full flex-col gap-2">
          {/* Top Row: Mute and Keypad */}
          <div className="flex w-full items-center gap-2">
            {/* Mute Button */}
            <Button
              variant="secondary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                toggleMute();
              }}
              disabled={!canUseAudioControls}
              className={cn(
                'flex-1 disabled:bg-gray-900/30 text-white h-10 rounded-xl',
                isMuted ? 'bg-gray-600 hover:bg-gray-700' : 'bg-gray-800/50 hover:bg-gray-800',
              )}
              aria-label={isMuted ? 'Unmute microphone' : 'Mute microphone'}
              aria-pressed={isMuted}
            >
              {isMuted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            </Button>

            {/* Keypad */}
            <Button
              variant="secondary"
              size="sm"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIsKeypadOpen(true);
              }}
              onMouseDown={(e) => e.stopPropagation()}
              disabled={!canUseKeypad}
              className={cn(
                'flex-1 disabled:bg-gray-900/30 text-white h-10 rounded-xl',
                'bg-gray-800/50 hover:bg-gray-800',
              )}
              aria-label="Open keypad"
              aria-haspopup="dialog"
              aria-expanded={isKeypadOpen}
            >
              <Hash className="h-4 w-4" />
            </Button>

            {/* Forward */}
            <Button
              variant="secondary"
              size="sm"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIsForwardPanelOpen(true);
              }}
              onMouseDown={(e) => e.stopPropagation()}
              disabled={status !== 'answered' || isForwardingActive}
              className={cn(
                'flex-1 disabled:bg-gray-900/30 text-white h-10 rounded-xl',
                'bg-gray-800/50 hover:bg-gray-800',
              )}
              aria-label="Forward call"
              aria-haspopup="dialog"
              aria-expanded={isForwardPanelOpen}
            >
              <PhoneForwarded className="h-4 w-4" />
            </Button>
          </div>

          {blockDialog}

          {/* Footer: End Call */}
          <Button
            variant="destructive"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleEndCall();
            }}
            onMouseDown={(e) => e.stopPropagation()}
            disabled={!canEndCall}
            className="w-full bg-red-600 hover:bg-red-700 disabled:bg-gray-800 disabled:text-gray-500 disabled:shadow-none text-white h-12 rounded-xl shadow-lg shadow-red-600/30 flex items-center justify-center gap-2"
            aria-label={canEndCall ? 'End call' : endCallButtonText}
          >
            <PhoneOff className="h-4 w-4" />
            {endCallButtonText}
          </Button>
        </div>

        {/* Keypad Slide Overlay (mockup-style) */}
        <div
          className={cn(
            'absolute inset-0 z-50 flex flex-col bg-gray-950 p-5 transition-transform duration-300 ease-out overflow-y-auto',
            isKeypadOpen ? 'translate-y-0' : 'translate-y-full',
          )}
          role="dialog"
          aria-label="Keypad"
          aria-hidden={!isKeypadOpen}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="flex w-full flex-col gap-4">
            <div className="flex h-10 w-full items-center justify-center border-b border-gray-800 text-white">
              <span className="font-mono text-2xl tracking-wider tabular-nums">
                {keypadDigits || ' '}
              </span>
            </div>

            <div className="grid grid-cols-3 gap-3">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'].map((digit) => (
                <button
                  key={digit}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePressKeypadDigit(digit);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-gray-800 bg-gray-900 text-base font-medium text-white hover:bg-gray-800 active:bg-blue-600 active:border-blue-500"
                  aria-label={`Send digit ${digit}`}
                >
                  {digit}
                </button>
              ))}
            </div>
          </div>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              closeKeypad();
            }}
            onMouseDown={(e) => e.stopPropagation()}
            className="mt-auto w-full rounded-xl px-3 py-2 text-center text-sm font-semibold text-blue-400 hover:text-blue-300 hover:bg-gray-900/40"
          >
            Hide Keypad
          </button>
        </div>

        {forwardCallPanel}
      </div>
      {microphonePermissionDialog}
    </div>
  );
};

export default FloatingStatusBar;
